import type { CSSProperties } from 'react';

/**
 * Shared typed shape for widget style settings, mirrored on the public API.
 * Any extension here must also be reflected in `widgetStylesSchema` in the backend.
 */
export interface WidgetStyles {
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  borderColor?: string;
  /** Background color of the section wrapping the widget (outside the card). */
  sectionBackgroundColor?: string;
  borderRadius?: number;
  padding?: number;
  opacity?: number;
}

/**
 * Default values used by the live preview and by the "Auf Standard zurücksetzen"
 * button. Kept in sync with the fallbacks in wunschgenau.de/OpeningHoursWidget.jsx.
 */
export const DEFAULT_WIDGET_STYLES: Required<
  Pick<
    WidgetStyles,
    | 'backgroundColor'
    | 'textColor'
    | 'accentColor'
    | 'borderColor'
    | 'sectionBackgroundColor'
    | 'borderRadius'
    | 'padding'
    | 'opacity'
  >
> = {
  backgroundColor: '#1B3835',
  textColor: '#EAD4B0',
  accentColor: '#E2C398',
  borderColor: '#2A4A47',
  sectionBackgroundColor: '#142827',
  borderRadius: 16,
  padding: 16,
  opacity: 1,
};

export type PlacementSlot =
  | 'before-footer'
  | 'after-header'
  | 'before-content'
  | 'after-content'
  | 'footer';

export interface WidgetPlacement {
  slot?: PlacementSlot;
  visibility?: {
    desktop?: boolean;
    tablet?: boolean;
    mobile?: boolean;
  };
}

export const PLACEMENT_SLOT_OPTIONS: Array<{ value: PlacementSlot; label: string; description: string }> = [
  { value: 'before-footer', label: 'Vor dem Footer', description: 'Unterhalb des Hauptinhalts, direkt über dem Footer (Standard)' },
  { value: 'after-header', label: 'Nach dem Hero', description: 'Direkt unterhalb des Hero-Bereichs, als erster Block im Inhalt' },
  { value: 'before-content', label: 'Am Seitenanfang', description: 'Zu Beginn des Hauptinhalts (nach Hero, vor den ersten Sektionen)' },
  { value: 'after-content', label: 'Am Seitenende', description: 'Am Ende des Hauptinhalts, vor Footer-Elementen' },
  { value: 'footer', label: 'Im Footer', description: 'Als Teil des Footers integriert' },
];

/**
 * Translate a WidgetStyles object into an inline CSS style object.
 * Kept here so the portal preview and the customer-site renderer stay in sync.
 */
export function stylesToCSS(styles: WidgetStyles | undefined): CSSProperties {
  if (!styles) return {};
  const css: CSSProperties = {};
  if (styles.backgroundColor) css.backgroundColor = styles.backgroundColor;
  if (styles.textColor) css.color = styles.textColor;
  if (styles.borderColor) css.border = `1px solid ${styles.borderColor}`;
  if (typeof styles.borderRadius === 'number') css.borderRadius = `${styles.borderRadius}px`;
  if (typeof styles.padding === 'number') css.padding = `${styles.padding}px`;
  if (typeof styles.opacity === 'number') css.opacity = styles.opacity;
  return css;
}
